import { Satisfaction } from 'lib-shared-legacy-types';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { formValueSelector, submit } from 'redux-form';

import { SatisfactionState } from 'components/settings/reducers/sections/satisfaction';
import { withRouter } from 'hocs/router';

import actions from '../../../actions/satisfaction';
import ChatCustomization, {
  ChatCustomizationOwnPropsWithLanguage,
  InitialValues,
} from '../../../components/sections/satisfaction/components/ChatCSAT/ChatCustomization';
import { CustomizationPageSelectors } from '../../../components/sections/satisfaction/components/common/selectors';

const {
  satisfactionLoadChatGlobalSettingsRequest,
  satisfactionLoadChatLanguageLevelSettingsRequest,
  satisfactionSaveChatLanguageLevelSettingsRequest,
} = actions;

const FORM_NAME = 'satisfactionChatCustomization';

const selector = formValueSelector(FORM_NAME);

type StateFromChatCustomizationPerspective = {
  [key: string]: any;
  settings: { satisfaction: SatisfactionState };
};
const mapStateToProps = (
  state: StateFromChatCustomizationPerspective,
  ownProps: ChatCustomizationOwnPropsWithLanguage,
) => {
  const {
    params: { language },
  } = ownProps;
  const { values, asyncStatus } =
    state.settings.satisfaction.customization.widgetchat.languagesLevel;

  let isSetup: boolean | null = null;
  let id: string | null = null;
  if (asyncStatus === 'succeeded') {
    const translation = values?.find((t) => t.language === language);
    id = translation?.id || null;
    isSetup = translation === undefined;
  }

  const initialValues: InitialValues = {
    customization: CustomizationPageSelectors.initialChatValuesSelector(state, {
      settingsId: id,
    }),
  };

  return {
    ratingQuestionPreview: selector(state, 'customization.rating_question'),
    ratingStylePreview: selector(state, 'customization.look_and_feel'),
    followUpQuestionPreview: selector(
      state,
      'customization.rating_follow_up_question',
    ),
    placeholderTextPreview: selector(
      state,
      'customization.rating_placeholder_text',
    ),
    thankYouMessagePreview: selector(
      state,
      'customization.rating_completed_message',
    ),
    id,
    isSetup,
    language,
    initialValues,
    asyncStatus,
  };
};

const mapDispatchToProps = (
  dispatch: Dispatch,
  ownProps: ChatCustomizationOwnPropsWithLanguage,
) => {
  const {
    params: { language },
  } = ownProps;

  return {
    loadGlobalSettings: () =>
      dispatch(satisfactionLoadChatGlobalSettingsRequest('widgetchat')),
    loadLanguageSettings: () =>
      dispatch(satisfactionLoadChatLanguageLevelSettingsRequest('widgetchat')),
    dispatchOnSubmit:
      (id: string | null) =>
      (fields: Partial<Satisfaction.ChatCsatSettings>) => {
        dispatch(
          satisfactionSaveChatLanguageLevelSettingsRequest({
            ...fields,
            language,
            channel: 'widgetchat',
            id,
            level: 'language',
          }),
        );
      },
    onChange: ({ enabled }: { enabled: boolean | undefined }) => {
      // This is unfortunately the only way we can trigger a submission when
      // the `enabled` hidden field has been added. It is not possible
      // to call `change()` and `submit()` sequentially.
      if (enabled !== undefined) {
        dispatch(submit(FORM_NAME));
      }
    },
  };
};

const mergeProps = (
  stateProps: ChatCustomizationStateProps,
  dispatchProps: ChatCustomizationDispatchProps,
  ownProps: ChatCustomizationOwnPropsWithLanguage,
) => {
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onSubmit: dispatchProps.dispatchOnSubmit(stateProps.id),
  };
};

export type ChatCustomizationMergeProps = ReturnType<typeof mergeProps>;
export type ChatCustomizationStateProps = ReturnType<typeof mapStateToProps>;
export type ChatCustomizationDispatchProps = ReturnType<
  typeof mapDispatchToProps
>;

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps, mergeProps)(ChatCustomization),
);
